<template>
  <section class="pending-notification-diagnostics">
    <div class="diagnostics-heading">
      <div>
        <h3>承認待ちリマインドの稼働確認</h3>
        <p>会社営業日に1日1回だけ実行します。実行する時刻は自社設定の就業時間で決まります。下の送信結果で実行有無と失敗理由を確認できます。</p>
      </div>
      <CBadge :color="statusColor">{{ statusLabel }}</CBadge>
    </div>

    <!--
      節の頭に出すのは、開いたときの読み込みの失敗だけ。［状態を再確認］［テスト送信］の
      結果はそのボタンのすぐ下に出す（間に状態の帯と11項目の表がある）。
    -->
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <div v-if="loading" class="diagnostics-loading">
      <CSpinner color="primary" size="sm"/>
      <span>通知状態を確認中…</span>
    </div>

    <template v-else>
      <CAlert :color="messageColor" class="diagnostics-message">
        {{ status.message || '通知状態を取得できませんでした。' }}
      </CAlert>

      <dl class="diagnostics-grid">
        <div><dt>通知設定</dt><dd>{{ yesNo(status.notification_enabled) }}</dd></div>
        <div><dt>契約機能</dt><dd>{{ yesNo(status.slack_feature_enabled) }}</dd></div>
        <div><dt>Botトークン</dt><dd>{{ configured(status.slack_token_configured) }}</dd></div>
        <div><dt>通知先チャンネル</dt><dd>{{ configured(status.slack_channel_configured) }}</dd></div>
        <div><dt>本日の営業日判定</dt><dd>{{ status.business_day ? '営業日' : '休日' }}</dd></div>
        <div><dt>現在の承認待ち</dt><dd>{{ numberLabel(status.pending_count) }}件</dd></div>
        <div><dt>本日の送信結果</dt><dd>{{ statusLabel }}</dd></div>
        <div><dt>試行回数</dt><dd>{{ numberLabel(status.attempt_count) }}回</dd></div>
        <div><dt>最終試行</dt><dd>{{ dateTimeLabel(status.last_attempt_at) }}</dd></div>
        <div><dt>送信完了</dt><dd>{{ dateTimeLabel(status.sent_at) }}</dd></div>
        <div class="diagnostics-grid__wide"><dt>直近の失敗理由</dt><dd>{{ display(status.last_error_reason) }}</dd></div>
      </dl>

      <div class="diagnostics-actions">
        <CButton color="secondary" variant="outline" :disabled="loading" @click="loadStatus({ pressed: true })">
          状態を再確認
        </CButton>
        <CButton color="primary" :disabled="!canSendTest || testing" @click="sendTest">
          <CSpinner v-if="testing" size="sm" class="mr-2"/>
          テスト送信
        </CButton>
      </div>
      <ActionResultMessage :text="actionMessage.text" :type="actionMessage.type" />
      <p v-if="!canSendTest" class="diagnostics-note">
        テスト送信には、契約機能・承認待ち通知・Botトークン・通知先チャンネルのすべての設定が必要です。
      </p>
    </template>
  </section>
</template>

<script>
import api from '@/services/companySettings'
import ActionResultMessage from '@/components/ActionResultMessage.vue'

const emptyStatus = () => ({
  notification_enabled: false,
  slack_feature_enabled: false,
  slack_token_configured: false,
  slack_channel_configured: false,
  business_day: false,
  pending_count: 0,
  delivery_status: '',
  attempt_count: 0,
  last_attempt_at: null,
  sent_at: null,
  last_error_code: '',
  last_error_reason: '',
  message: ''
})

export default {
  name: 'PendingApprovalNotificationStatus',
  components: { ActionResultMessage },
  data () {
    return {
      loading: false,
      testing: false,
      // 開いたときの読み込みの失敗だけ（節の頭に出す）。
      errorMessage: '',
      // ［状態を再確認］［テスト送信］の結果。ボタンのすぐ下に出す。
      actionMessage: { type: 'success', text: '' },
      status: emptyStatus()
    }
  },
  computed: {
    canSendTest () {
      return Boolean(
        this.status.slack_feature_enabled &&
        this.status.notification_enabled &&
        this.status.slack_token_configured &&
        this.status.slack_channel_configured
      )
    },
    statusLabel () {
      const labels = {
        SENT: '送信済み',
        SENDING: '送信処理中',
        FAILED: '送信失敗',
        NO_PENDING: '承認待ち0件',
        HOLIDAY: '会社休日',
        PENDING: '送信待ち'
      }
      return labels[this.status.delivery_status] || '未実行'
    },
    statusColor () {
      const colors = {
        SENT: 'success',
        SENDING: 'warning',
        FAILED: 'danger',
        NO_PENDING: 'info',
        HOLIDAY: 'secondary',
        PENDING: 'warning'
      }
      return colors[this.status.delivery_status] || 'secondary'
    },
    messageColor () {
      if (this.status.delivery_status === 'FAILED') return 'danger'
      if (this.status.delivery_status === 'SENT') return 'success'
      if (!this.status.notification_enabled || !this.status.slack_feature_enabled) return 'warning'
      if (!this.status.slack_token_configured || !this.status.slack_channel_configured) return 'warning'
      return 'info'
    }
  },
  mounted () {
    this.loadStatus()
  },
  methods: {
    /**
     * @param options.pressed ［状態を再確認］で呼ばれたとき true。読めなかった理由を
     *   ボタンのすぐ下に出す。開いたときの読み込みは節の頭に出す。
     * @param options.quiet テスト送信のあとの読み直しでは true。**表とボタンを
     *   作り直さない。**作り直すと、ボタンのすぐ下に出した結果の文も作り直され、
     *   そこまで画面が動かない。
     */
    async loadStatus (options) {
      const pressed = Boolean(options && options.pressed)
      if (!(options && options.quiet)) this.loading = true
      this.errorMessage = ''
      if (pressed) this.actionMessage = { type: 'success', text: '' }
      try {
        const result = await api.pendingApprovalStatus()
        this.status = Object.assign(emptyStatus(), result || {})
      } catch (error) {
        this.status = emptyStatus()
        const detail = this.errorDetail(error, '承認待ち通知の状態を取得できませんでした。')
        if (pressed) this.actionMessage = { type: 'danger', text: detail }
        else this.errorMessage = detail
      } finally {
        this.loading = false
      }
    },
    async sendTest () {
      if (!this.canSendTest || this.testing) return
      this.testing = true
      this.actionMessage = { type: 'success', text: '' }
      try {
        await api.testPendingApprovalNotification()
        this.actionMessage = { type: 'success', text: '設定中の承認待ち通知チャンネルへテストメッセージを送信しました。' }
        await this.loadStatus({ quiet: true })
      } catch (error) {
        this.actionMessage = { type: 'danger', text: this.errorDetail(error, 'テスト送信に失敗しました。Botの権限とチャンネル参加状況も確認してください。') }
      } finally {
        this.testing = false
      }
    },
    errorDetail (error, fallback) {
      const data = error && error.response && error.response.data
      return (data && (data.message)) || fallback
    },
    yesNo (value) {
      return value ? '有効' : '無効'
    },
    configured (value) {
      return value ? '設定済み' : '未設定'
    },
    numberLabel (value) {
      return Number.isFinite(Number(value)) ? Number(value) : 0
    },
    display (value) {
      return value == null || String(value).trim() === '' ? 'なし' : value
    },
    dateTimeLabel (value) {
      if (!value) return '未記録'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return String(value)
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).format(parsed)
    }
  }
}
</script>

<style scoped>
.pending-notification-diagnostics { margin-top: 18px; padding: 18px; border: 1px solid #dce8ef; border-radius: 14px; background: #f8fbfd; }
.diagnostics-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
.diagnostics-heading h3 { margin: 0 0 5px; color: #17384d; font-size: 16px; }
.diagnostics-heading p { margin: 0; color: #6a7f8c; font-size: 12px; line-height: 1.65; }
.diagnostics-loading { display: flex; align-items: center; gap: 10px; min-height: 88px; color: #617887; }
.diagnostics-message { margin-bottom: 14px; }
.diagnostics-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin: 0; }
.diagnostics-grid > div { min-width: 0; padding: 12px; border: 1px solid #e1e9ee; border-radius: 10px; background: #fff; }
.diagnostics-grid dt { margin-bottom: 5px; color: #617887; font-size: 12px; font-weight: 800; }
.diagnostics-grid dd { margin: 0; color: #17384d; font-size: 13px; font-weight: 700; overflow-wrap: anywhere; }
.diagnostics-grid__wide { grid-column: span 2; }
.diagnostics-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
.diagnostics-note { margin: 10px 0 0; color: #718592; font-size: 12px; text-align: right; }
@media (max-width: 991px) { .diagnostics-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) {
  .pending-notification-diagnostics { padding: 14px; }
  .diagnostics-heading { display: grid; }
  .diagnostics-grid { grid-template-columns: 1fr; }
  .diagnostics-grid__wide { grid-column: auto; }
  .diagnostics-actions { display: grid; }
  .diagnostics-actions .btn { width: 100%; }
  .diagnostics-note { text-align: left; }
}
</style>
